import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ConciergeBell, Leaf, CakeSlice, Package, Croissant } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { PACKAGES, SERVICE_PROMISES, EXTRA_SERVICES } from "@/data/content";

const INCLUDE_ICONS = { bell: ConciergeBell, leaf: Leaf, cake: CakeSlice, box: Package, croissant: Croissant };

export const Packages = ({ onEnquire, enquiryHref = "#enquiry", tone = "light" }) => {
  const [open, setOpen] = useState(null);
  const light = tone === "light";

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(null);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <section
      id="packages"
      className={`scroll-mt-28 py-24 sm:py-32 ${light ? "bg-paper text-ink" : "bg-obsidian text-cream"}`}
      data-testid="packages-section"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-8">
            <img
              src="/images/logo-filmtv-banner.png"
              alt="Root2Shoot Sustainable Film and TV Catering"
              className="mb-6 h-12 w-auto sm:h-14"
              loading="lazy"
              decoding="async"
            />
            <p className={`${light ? "label-mono-ink" : "label-mono"} flex items-center gap-4`}>
              Catering Packages
              <span className={`h-px w-20 ${light ? "bg-gold-deep/60" : "bg-gold/50"}`} aria-hidden="true" />
            </p>
            <h2 className={`mt-6 font-editorial text-4xl leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl ${light ? "text-ink" : "text-cream"}`}>
              The service around
              <br />
              <span className="text-gold">the food</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-4">
            <p className={`text-base leading-relaxed ${light ? "text-ink/70" : "text-sage"}`}>
              Great food is only part of the story. We provide a complete, reliable catering
              service built around the needs of your production.
            </p>
          </Reveal>
        </div>

        <div className="mt-20 grid gap-14 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {PACKAGES.map((pkg, i) => (
            <Reveal key={pkg.tier} delay={i * 0.1} className={i === 1 ? "lg:mt-16" : i === 2 ? "lg:mt-32" : ""}>
              <article data-testid={`package-tier-${pkg.tier}`}>
                <button
                  onClick={() => setOpen(pkg)}
                  className="group relative block w-full text-left"
                  aria-haspopup="dialog"
                  aria-label={`View full details of the ${pkg.name} package`}
                  data-testid={`package-details-${pkg.tier}`}
                >
                  <span
                    className={`absolute -inset-0 translate-x-3 translate-y-3 border transition-transform duration-500 group-hover:translate-x-4 group-hover:translate-y-4 ${
                      light ? "border-gold-deep/60" : "border-gold/50"
                    }`}
                    aria-hidden="true"
                  />
                  <img
                    src={pkg.poster}
                    alt={`${pkg.name} package artwork — ${pkg.price} ${pkg.priceNote}, Root2Shoot film and TV catering`}
                    className="relative w-full"
                    loading="lazy"
                    decoding="async"
                    data-testid={`package-image-${pkg.tier}`}
                  />
                  <span className="absolute bottom-4 right-4 bg-noir/85 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    View Details
                  </span>
                </button>
                <div className={`mt-7 border-t pt-5 ${light ? "border-gold-deep/40" : "border-gold/25"}`}>
                  <p className={light ? "label-mono-ink" : "label-mono"}>{pkg.number}</p>
                  <h3 className={`mt-2 font-editorial text-3xl tracking-tight ${light ? "text-ink" : "text-cream"}`}>{pkg.name}</h3>
                </div>
                <div className="mt-5 flex items-center gap-6">
                  <button onClick={() => setOpen(pkg)} className={light ? "link-more-ink" : "link-more"} data-testid={`package-more-${pkg.tier}`}>
                    Full Details <ArrowRight size={14} />
                  </button>
                  <a
                    href={enquiryHref}
                    onClick={() => onEnquire?.(pkg.tier)}
                    className={light ? "link-more-ink" : "link-more"}
                    data-testid={`package-contact-${pkg.tier}`}
                  >
                    Enquire <ArrowRight size={14} />
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-28">
          <div className={`border-y py-9 ${light ? "border-gold-deep/40" : "border-gold/25"}`} data-testid="package-values">
            <p className={`${light ? "label-mono-ink" : "label-mono"} mb-6`}>Every package includes</p>
            <ul className="flex flex-wrap items-center gap-x-7 gap-y-4">
              {SERVICE_PROMISES.map((s, i) => (
                <li key={s} className="flex items-center gap-7">
                  <span className={`font-display text-xl italic sm:text-2xl ${light ? "text-ink/85" : "text-cream/90"}`}>{s}</span>
                  {i < SERVICE_PROMISES.length - 1 && (
                    <span className={`h-1.5 w-1.5 rotate-45 ${light ? "bg-gold-deep/70" : "bg-gold/70"}`} aria-hidden="true" />
                  )}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 grid gap-12 sm:grid-cols-2">
            {EXTRA_SERVICES.map((x) => (
              <div key={x.id} className={`border-l-2 pl-7 ${light ? "border-gold-deep/60" : "border-gold/50"}`} data-testid={`extra-service-${x.id}`}>
                <h3 className={`font-editorial text-2xl tracking-tight sm:text-3xl ${light ? "text-ink" : "text-cream"}`}>{x.title}</h3>
                <p className={`mt-3 text-sm leading-relaxed sm:text-base ${light ? "text-ink/70" : "text-sage"}`}>{x.body}</p>
              </div>
            ))}
          </div>
          <p
            className={`mt-16 text-center font-mono text-[11px] uppercase tracking-[0.3em] ${light ? "text-ink/50" : "text-sage/60"}`}
            data-testid="package-footer-line"
          >
            Good Food&nbsp;&nbsp;|&nbsp;&nbsp;Lower Impact&nbsp;&nbsp;|&nbsp;&nbsp;Brighter Tomorrows
          </p>
        </Reveal>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-noir/85 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(null)}
            data-testid="package-modal-overlay"
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={`${open.name} package details`}
              className="relative max-h-[88vh] w-full max-w-2xl overflow-y-auto border border-gold-deep/50 bg-paper text-ink"
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              data-testid="package-modal"
            >
              <button
                onClick={() => setOpen(null)}
                className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center border border-ink/30 bg-paper/85 text-ink transition-colors hover:bg-obsidian hover:text-cream"
                aria-label="Close package details"
                data-testid="package-modal-close"
              >
                <X size={18} />
              </button>
              <img src={open.image} alt={open.imageAlt} className="aspect-[16/8] w-full object-cover" />
              <div className="p-8 sm:p-10">
                <p className="font-display text-xl italic text-gold-deep">{open.number}</p>
                <h3 className="mt-1 font-editorial text-4xl tracking-tight text-ink sm:text-5xl">{open.name}</h3>
                <p className="mt-5 text-base leading-relaxed text-ink/75">{open.tagline}</p>
                <div className="mt-8 flex items-center gap-3" aria-hidden="true">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold-deep">Includes</span>
                  <span className="h-px flex-1 bg-gold-deep/40" />
                </div>
                <ul className="mt-2 divide-y divide-gold-deep/25">
                  {open.includes.map((inc) => {
                    const Icon = INCLUDE_ICONS[inc.icon];
                    return (
                      <li key={inc.title} className="flex items-start gap-4 py-4">
                        <Icon size={22} className="mt-1 shrink-0 text-gold-deep" />
                        <div>
                          <p className="font-serif text-lg text-ink">{inc.title}</p>
                          <p className="mt-1 text-sm leading-relaxed text-ink/65">{inc.body}</p>
                          {inc.sub && (
                            <div className="mt-3">
                              <p className="text-sm font-semibold text-gold-deep">{inc.sub.title}</p>
                              <p className="mt-1 text-sm leading-relaxed text-ink/65">{inc.sub.body}</p>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-7 flex flex-wrap items-center gap-4">
                  <span
                    className="bg-gold px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-noir"
                    data-testid={`package-complete-${open.tier}`}
                  >
                    It is a complete price
                  </span>
                  <a
                    href={enquiryHref}
                    onClick={() => {
                      onEnquire?.(open.tier);
                      setOpen(null);
                    }}
                    className="btn-ink"
                    data-testid={`package-cta-${open.tier}`}
                  >
                    Enquire About {open.name}
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
